import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import {
  selectUserToken,
  setDisplayErrorSnackBar,
} from "../../../../../features/userSlice";
import { useSearchParams } from "react-router-dom";
import { STOperationEndPointLogic } from "../../../../../logic/PaidTrafficManagement/SearchTermOperation/searchTermOperationEndPointLogic";
import { useState } from "react";
import {
  resetScopesTabFiltersAndModals,
  resetStateOfScopesTab,
  setCampaignsWithoutScopeData,
  setEditScopeLoading,
  setIsScopesLoading,
  setIsScopesTabDataFetched,
  setNewScopeLoading,
  setRemoveNegativeLoadingCampaignLoading,
  setRemoveNegativeLoadingForBothLoading,
  setScopesOfSTOperation,
  setSelectedRowsForNegatives,
  setSelectedRowsForNegativesCampaign,
  setSelectedSTOperationAccount,
  setSingleRowIDPassToModal,
  setSingleRowIDPassToModalForCampaign,
} from "../../../../../features/scopesTabSlice";
import {
  resetStateOfSearchTermTab,
  setConfirmLoaderForContradictingSTDefinition,
  setSelectedRowsForMissingCrossNegatives,
  setToggledNegativesForMissingCrossNegatives,
} from "../../../../../features/searchTermTabSlice";
import { useSearchTermsTab } from "./useSearchTermsTab";
import { resetStateOfAiRecomendationTab } from "../../../../../features/aiRecomendationTabSlice";

// Constants
const QUERY_IDS = {
  ACCOUNTS: "5761660895625216",
  CAMPAIGNS_WITHOUT_SCOPE: "5755374237908992",
  NEGATIVE_DEFINITION_BOTH: "5760334488928256",
  NEGATIVE_DEFINITION_CAMPAIGN: "5719366876266496",
} as const;

const ACCOUNT_FILTERS = {
  TRAFFIC_SOURCE_SHARED_UID: 1,
  IS_ACTIVE: 2,
} as const;

type NegativeTermType = "adgroups" | "campaigns" | "ContradictingST";

export const useScopes = () => {
  const oauthTokenHeader = useSelector(selectUserToken) as any;
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const [accountsLoading, setAccountsLoading] = useState(false);

  const selectedSharedUid = searchParams.get("selectedSite") || "";
  const selectedAccountSharedUid = searchParams.get("account") || "";

  const { getContradictingSTDefinitions } = useSearchTermsTab();

  // Clear all state when changing site
  const clearParams = () => {
    setSearchParams({});
    dispatch(resetStateOfScopesTab()); // Reset STO scopes tab state
    dispatch(resetStateOfSearchTermTab()); // Reset STO search term tab state
    dispatch(resetStateOfAiRecomendationTab());
  };

  // Clear state when changing account (preserve site selection)
  const clearParamsForAccountSelection = () => {
    setSearchParams({});
    dispatch(setSelectedSTOperationAccount(""));
    dispatch(setIsScopesTabDataFetched(false));
    dispatch(setScopesOfSTOperation([]));
    dispatch(setCampaignsWithoutScopeData([]));

    dispatch(resetScopesTabFiltersAndModals()); // Reset  scopes tab filters, modals and selections states
    dispatch(resetStateOfSearchTermTab()); // Reset STO search term tab state
    dispatch(resetStateOfAiRecomendationTab());
  };

  //clear params of tables(like selected filter, row, pagination and passed id)
  const clearParamsOfTables = () => {
    dispatch(resetScopesTabFiltersAndModals()); // Reset  scopes tab filters, modals and selections state
    dispatch(resetStateOfSearchTermTab()); // Reset STO search term tab state
    dispatch(resetStateOfAiRecomendationTab());
  };

  // Fetch accounts for selected site
  const getAccountsBasedOnSite = async (site_shared_uid: string) => {
    setAccountsLoading(true);
    try {
      await STOperationEndPointLogic.getAccounts(
        oauthTokenHeader,
        QUERY_IDS.ACCOUNTS,
        site_shared_uid,
        ACCOUNT_FILTERS.TRAFFIC_SOURCE_SHARED_UID,
        ACCOUNT_FILTERS.IS_ACTIVE
      );
    } catch (error) {
      dispatch(
        setDisplayErrorSnackBar({
          message: "Failed to fetch accounts. Please try again.",
          type: "error",
        })
      );
    } finally {
      setAccountsLoading(false);
    }
  };

  // Fetch all scopes tab data in parallel
  const fetchScopesTabData = async (
    site_shared_uid: string,
    account_shared_uid?: string
  ) => {
    dispatch(setIsScopesLoading(true));
    dispatch(setIsScopesTabDataFetched(false));

    try {
      const accountUid = account_shared_uid || "";
      const [
        scopesRes,
        campaignsWithoutScopeRes,
        negativeDefinitionBothRes,
        negativeDefinitionInCampaignRes,
      ] = await Promise.all([
        STOperationEndPointLogic.getScopes(
          oauthTokenHeader,
          site_shared_uid,
          accountUid
        ),
        STOperationEndPointLogic.getCampaignsWithoutScope(
          oauthTokenHeader,
          QUERY_IDS.CAMPAIGNS_WITHOUT_SCOPE,
          site_shared_uid,
          accountUid
        ),
        STOperationEndPointLogic.getNegativeDefinitionBothListAndAdgroup(
          oauthTokenHeader,
          QUERY_IDS.NEGATIVE_DEFINITION_BOTH,
          site_shared_uid,
          accountUid
        ),
        STOperationEndPointLogic.getNegativeDefinitionInCampaign(
          oauthTokenHeader,
          QUERY_IDS.NEGATIVE_DEFINITION_CAMPAIGN,
          site_shared_uid,
          accountUid
        ),
      ]);

      const allResponses = {
        scopes: scopesRes,
        campaignsWithoutScope: campaignsWithoutScopeRes,
        negativeDefinitionBoth: negativeDefinitionBothRes,
        negativeDefinitionInCampaign: negativeDefinitionInCampaignRes,
      };

      return allResponses;
    } catch (error) {
      dispatch(
        setDisplayErrorSnackBar({
          message: "Failed to fetch scopes data. Please try again.",
          type: "error",
        })
      );
    } finally {
      dispatch(setIsScopesLoading(false));
      dispatch(setIsScopesTabDataFetched(true));
    }
  };

  // Clear selections and refetch data after removing negative terms
  const handleClearParams = async (type: NegativeTermType) => {
    // Clear selections and refetch data based on type
    if (type === "adgroups") {
      dispatch(setSelectedRowsForNegatives([]));
      dispatch(setSingleRowIDPassToModal(""));

      // Refetch adgroup data
      await STOperationEndPointLogic.getNegativeDefinitionBothListAndAdgroup(
        oauthTokenHeader,
        QUERY_IDS.NEGATIVE_DEFINITION_BOTH,
        selectedSharedUid,
        selectedAccountSharedUid
      );

      dispatch(setRemoveNegativeLoadingForBothLoading(false));
    } else if (type === "campaigns") {
      dispatch(setSelectedRowsForNegativesCampaign([]));
      dispatch(setSingleRowIDPassToModalForCampaign(""));

      // Refetch campaign data
      await STOperationEndPointLogic.getNegativeDefinitionInCampaign(
        oauthTokenHeader,
        QUERY_IDS.NEGATIVE_DEFINITION_CAMPAIGN,
        selectedSharedUid,
        selectedAccountSharedUid
      );

      dispatch(setRemoveNegativeLoadingCampaignLoading(false));
    } else if (type === "ContradictingST") {
      dispatch(setSelectedRowsForMissingCrossNegatives([])); // Clear selected rows
      dispatch(setToggledNegativesForMissingCrossNegatives([])); // Clear toggled negatives

      await getContradictingSTDefinitions();

      dispatch(setConfirmLoaderForContradictingSTDefinition(false));
    }
  };

  // Handle negative term removal
  const removeNegativeSearchTerm = async (
    reqBody: any,
    onClose: () => void,
    type: NegativeTermType
  ) => {
    try {
      onClose();

      // Set loading state
      if (type === "adgroups") {
        dispatch(setRemoveNegativeLoadingForBothLoading(true));
      }
      if (type === "campaigns") {
        dispatch(setRemoveNegativeLoadingCampaignLoading(true));
      }
      if (type === "ContradictingST") {
        dispatch(setConfirmLoaderForContradictingSTDefinition(true));
      }

      const response = await STOperationEndPointLogic.removeNegativeTerms(
        oauthTokenHeader,
        reqBody
      );

      if (!response) {
        throw new Error(
          "Failed to remove negative terms. Please try again later."
        );
      }

      const { reason, data } = response;

      // CHECK FOR ERROR REASONS FIRST - before accessing data.summary
      if (reason && reason !== "Success" && reason !== "Partial Success") {
        // This handles your case: reason: "terms: {'non_field_errors': [...]}"
        throw new Error(reason);
      }

      // NOW safely access summary with optional chaining and validation
      const summary = data?.summary;
      const results = data?.results || [];

      // Validate summary exists before using it
      if (!summary) {
        throw new Error(
          "Failed to remove negative terms. Please try again later."
        );
      }

      // const summary = data.summary;
      // const results = data.results || [];

      // Handle based on reason
      if (reason === "Success") {
        await handleClearParams(type);
        dispatch(
          setDisplayErrorSnackBar({
            message: `Successfully removed ${summary.successful} negative term(s)`,
            type: "success",
          })
        );
      } else if (reason === "Partial Success") {
        await handleClearParams(type);
        // Partial success - some succeeded, some failed
        const failedItems = results.filter((r: any) => r.status === "failed");
        const successfulItems = results.filter(
          (r: any) => r.status === "success"
        );

        // Show detailed error for failed items
        const errorDetails = failedItems
          .map((item: any) => {
            const term = item.term || "Unknown term";
            const error = item.error || "Unknown error";
            return `${term}: ${error}`;
          })
          .join("; ");

        dispatch(
          setDisplayErrorSnackBar({
            message: `Removed ${successfulItems.length}/${summary.total} term(s). ${failedItems.length} failed. ${errorDetails}`,
            type: "warning",
          })
        );

        dispatch(
          setDisplayErrorSnackBar({
            message: `Removed ${successfulItems.length}/${summary.total} term(s). ${failedItems.length} failed. ${errorDetails}`,
            type: "warning",
          })
        );
      }
    } catch (error) {
      // Reset loading state
      if (type === "adgroups") {
        dispatch(setRemoveNegativeLoadingForBothLoading(false));
      }
      if (type === "campaigns") {
        dispatch(setRemoveNegativeLoadingCampaignLoading(false));
      }

      if (type === "ContradictingST") {
        dispatch(setConfirmLoaderForContradictingSTDefinition(false));
      }

      // Extract error message - handle both string and object cases
      let errorMessage = "Failed to remove negative terms. Please try again.";

      const err = error as any;

      if (err?.parsedText?.reason) {
        errorMessage = err.parsedText.reason;
      } else if (err?.response?.data?.reason) {
        errorMessage = err.response.data.reason;
      } else if (err?.message && typeof err.message === "string") {
        errorMessage = err.message;
      } else if (typeof err === "string") {
        errorMessage = err;
      }

      dispatch(
        setDisplayErrorSnackBar({
          message: errorMessage,
          type: "error",
        })
      );
    }
  };

  //get campaigns without scope
  const campaignsWithoutScope = async () => {
    try {
      await STOperationEndPointLogic.getCampaignsWithoutScope(
        oauthTokenHeader,
        QUERY_IDS.CAMPAIGNS_WITHOUT_SCOPE,
        selectedSharedUid,
        selectedAccountSharedUid
      );
    } catch (error) {
      dispatch(
        setDisplayErrorSnackBar({
          message:
            "Failed to fetching campaigns without scope. Please try again.",
          type: "error",
        })
      );
    }
  };

  // get scope list
  const scopeList = async () => {
    try {
      await STOperationEndPointLogic.getScopes(
        oauthTokenHeader,
        selectedSharedUid,
        selectedAccountSharedUid
      );
    } catch (error) {
      dispatch(
        setDisplayErrorSnackBar({
          message: "Failed to fetching scopes. Please try again.",
          type: "error",
        })
      );
    }
  };

  //--link campaign to scope--
  const linkCampaignToScope = async (linkRequestBody: any, source: string) => {
    try {
      const response =
        await STOperationEndPointLogic.linkBetweenCampaignToScope(
          oauthTokenHeader,
          linkRequestBody
        );

      if (!response) {
        dispatch(
          setDisplayErrorSnackBar({
            message: "No response received from server.",
            type: "error",
          })
        );
        return;
      }

      // Handle based on response.status and flags
      const { success, status, partialSuccess, allFailed, reason, data } =
        response;

      if (success && status === 201) {
        // All records linked successfully

        // Refresh lists
        await campaignsWithoutScope();
        await scopeList();
        if (source === "AddScope") dispatch(setNewScopeLoading(false));
        if (source === "EditScope") dispatch(setEditScopeLoading(false));

        dispatch(
          setDisplayErrorSnackBar({
            message: `campaign's linked successfully`,
            type: "success",
          })
        );
      } else if (status === 207 && partialSuccess) {
        const items = data?.data || [];
        const successCount = items.filter(
          (item: any) => item.created === true || item.updated === true
        ).length;
        const failCount = items.filter(
          (item: any) => item.created === false && item.updated === false
        ).length;

        // Refresh lists
        await campaignsWithoutScope();
        await scopeList();
        if (source === "AddScope") dispatch(setNewScopeLoading(false));
        if (source === "EditScope") dispatch(setEditScopeLoading(false));

        dispatch(
          setDisplayErrorSnackBar({
            message: `${successCount} linked/updated, ${failCount} failed`,
            type: "warning",
          })
        );
      } else if (status === 207 && allFailed) {
        if (source === "AddScope") dispatch(setNewScopeLoading(false));
        if (source === "EditScope") dispatch(setEditScopeLoading(false));

        dispatch(
          setDisplayErrorSnackBar({
            message: "All campaign link operations failed.",
            type: "error",
          })
        );
      } else if (status === 400) {
        if (source === "AddScope") dispatch(setNewScopeLoading(false));
        if (source === "EditScope") dispatch(setEditScopeLoading(false));

        dispatch(
          setDisplayErrorSnackBar({
            message: reason || "Failed to link campaign to scope",
            type: "error",
          })
        );
      } else {
        if (source === "AddScope") dispatch(setNewScopeLoading(false));
        if (source === "EditScope") dispatch(setEditScopeLoading(false));

        dispatch(
          setDisplayErrorSnackBar({
            message:
              reason || `Unexpected response (status ${status || "unknown"})`,
            type: "error",
          })
        );
      }
    } catch (error: any) {
      if (source === "AddScope") dispatch(setNewScopeLoading(false));
      if (source === "EditScope") dispatch(setEditScopeLoading(false));

      dispatch(
        setDisplayErrorSnackBar({
          message: error?.reason || "Failed to link campaign to scope",
          type: "error",
        })
      );
    }
  };

  return {
    clearParams,
    clearParamsForAccountSelection,
    clearParamsOfTables,
    accountsLoading,
    getAccountsBasedOnSite,
    fetchScopesTabData,
    removeNegativeSearchTerm,
    campaignsWithoutScope,
    scopeList,
    linkCampaignToScope,
  };
};
